import { usePatchInstructorCheckInTimeMutation } from "@/modules/attendance/presentation/api/queries";
import {
  EditCheckInTimeDialog,
  type EditCheckInTimeTarget,
} from "@/modules/attendance/presentation/ui/EditCheckInTimeDialog";
import { parseYearMonthFromDateYyyyMmDd } from "@/modules/reports/domain/lib/parse-year-month";
import { useInstructorPayrollBatchByMonthQuery } from "@/modules/reports/presentation/api/queries";
import { Button, Label } from "@devhop/ui";
import { ClockIcon, Loader2Icon, Trash2Icon, UserPlusIcon } from "lucide-react";
import { useMemo, useState } from "react";
import { config } from "@/shared/lib/config";
import { formatDateTime } from "@/shared/lib/date-time";
import { fetchLookupForInfinite, hydrateLookupItem } from "@/shared/lib/utils";
import { InfiniteCombobox } from "@/shared/ui/InfiniteCombobox";
import {
  useAddAssistInstructor,
  useAssistInstructorsQuery,
  useRemoveAssistInstructor,
} from "../api/queries";

type RoomBookingAssistInstructorsSectionProps = {
  bookingId: string;
  bookingDate: string;
  bookingStatus: "scheduled" | "completed" | "cancelled";
  canEdit: boolean;
};

export function RoomBookingAssistInstructorsSection({
  bookingId,
  bookingDate,
  bookingStatus,
  canEdit,
}: RoomBookingAssistInstructorsSectionProps) {
  const { data, isLoading } = useAssistInstructorsQuery(bookingId);
  const addAssist = useAddAssistInstructor(bookingId);
  const removeAssist = useRemoveAssistInstructor(bookingId);
  const patchTimeMutation = usePatchInstructorCheckInTimeMutation();
  const [instructorId, setInstructorId] = useState("");
  const [editTimeOpen, setEditTimeOpen] = useState(false);
  const [editTimeTarget, setEditTimeTarget] =
    useState<EditCheckInTimeTarget | null>(null);

  const yearMonth = useMemo(() => {
    try {
      return parseYearMonthFromDateYyyyMmDd(bookingDate);
    } catch {
      return null;
    }
  }, [bookingDate]);

  const payrollBatchQuery = useInstructorPayrollBatchByMonthQuery(
    yearMonth?.year ?? null,
    yearMonth?.month ?? null,
  );
  const payrollLocked = !!payrollBatchQuery.data;

  const rows = data?.data ?? [];
  const canMutate =
    canEdit &&
    !payrollLocked &&
    (bookingStatus === "scheduled" || bookingStatus === "completed");
  const alreadyIds = new Set(rows.map((r) => r.instructorId));

  const handleAdd = async () => {
    if (!instructorId || alreadyIds.has(instructorId)) return;
    await addAssist.mutateAsync(instructorId);
    setInstructorId("");
  };

  const handleOpenEditTime = (row: {
    id: string;
    instructorName: string;
    checkedInAt: string;
  }) => {
    setEditTimeTarget({
      id: row.id,
      label: row.instructorName,
      checkedInAt: row.checkedInAt,
    });
    setEditTimeOpen(true);
  };

  const handleConfirmEditTime = async (values: {
    id: string;
    checkedInAt: string;
  }) => {
    await patchTimeMutation.mutateAsync({
      checkInId: values.id,
      checkedInAt: values.checkedInAt,
    });
    setEditTimeOpen(false);
    setEditTimeTarget(null);
  };

  const isMutating =
    addAssist.isPending ||
    removeAssist.isPending ||
    patchTimeMutation.isPending;

  const hint = payrollLocked
    ? "\u0e9b\u0eb4\u0e94\u0e87\u0ea7\u0e94 payroll \u0ec1\u0ea5\u0ec9\u0ea7 \u2014 \u0e9a\u0ecd\u0ec8\u0eaa\u0eb2\u0ea1\u0eb2\u0e94\u0ec0\u0e9e\u0eb5\u0ec8\u0ea1/\u0ea5\u0ebb\u0e9a\u0e84\u0eb9\u0e8a\u0ec8\u0ea7\u0e8d\u0eaa\u0ead\u0e99\u0ec4\u0e94\u0ec9"
    : bookingStatus === "completed"
      ? "\u0e9a\u0eb1\u0e99\u0e97\u0eb6\u0e81\u0e84\u0eb9\u0ec3\u0e99\u0ea5\u0eb0\u0e9a\u0ebb\u0e9a\u0e97\u0eb5\u0ec8\u0ea1\u0eb2\u0e8a\u0ec8\u0ea7\u0e8d\u0eaa\u0ead\u0e99 \u2014 \u0e84\u0eb4\u0e94\u0e84\u0ec8\u0eb2\u0eaa\u0ead\u0e99\u0e95\u0ecd\u0ec8\u0e84\u0eb1\u0ec9\u0e87"
      : bookingStatus === "scheduled"
        ? "\u0ec1\u0e99\u0eb0\u0e99\u0eb3\u0e9a\u0eb1\u0e99\u0e97\u0eb6\u0e81\u0eab\u0ebc\u0eb1\u0e87\u0eaa\u0ead\u0e99\u0ec0\u0eaa\u0eb1\u0e94 / \u0ec0\u0ea7\u0ea5\u0eb2 complete"
        : "\u0e81\u0eb2\u0e99\u0e88\u0ead\u0e87\u0e96\u0eb7\u0e81\u0e8d\u0ebb\u0e81\u0ec0\u0ea5\u0eb5\u0e81 \u2014 \u0ec0\u0e9e\u0eb5\u0ec8\u0ea1\u0e84\u0eb9\u0e8a\u0ec8\u0ea7\u0e8d\u0e9a\u0ecd\u0ec8\u0ec4\u0e94\u0ec9";

  return (
    <div className="mt-6 rounded-xl border bg-card p-6">
      <div className="mb-4">
        <h3 className="font-semibold text-lg">
          {"\u0e84\u0eb9\u0e8a\u0ec8\u0ea7\u0e8d\u0eaa\u0ead\u0e99 (\u0ec3\u0e99\u0ea5\u0eb0\u0e9a\u0ebb\u0e9a)"}
        </h3>
        <p className="mt-1 text-muted-foreground text-sm">{hint}</p>
      </div>

      {isLoading ? (
        <div className="flex items-center gap-2 text-muted-foreground text-sm">
          <Loader2Icon className="size-4 animate-spin" />
          {"\u0e81\u0eb3\u0ea5\u0eb1\u0e87\u0ec2\u0eab\u0ebc\u0e94..."}
        </div>
      ) : rows.length === 0 ? (
        <p className="text-muted-foreground text-sm">
          {"\u0e8d\u0eb1\u0e87\u0e9a\u0ecd\u0ec8\u0ea1\u0eb5\u0e84\u0eb9\u0e8a\u0ec8\u0ea7\u0e8d\u0eaa\u0ead\u0e99"}
        </p>
      ) : (
        <ul className="mb-4 divide-y rounded-md border">
          {rows.map((row) => (
            <li
              key={row.id}
              className="flex items-center justify-between gap-3 px-3 py-2"
            >
              <div className="min-w-0">
                <p className="font-medium">{row.instructorName}</p>
                <p className="text-muted-foreground text-xs">
                  {formatDateTime(row.checkedInAt)}
                  {row.customerPriceKip != null && row.customerPriceKip > 0
                    ? ` · ${row.customerPriceKip.toLocaleString("lo-LA")} ກີບ/ຄັ້ງ`
                    : ""}
                </p>
              </div>
              {canMutate ? (
                <div className="flex shrink-0 items-center gap-1">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={isMutating}
                    onClick={() => handleOpenEditTime(row)}
                    aria-label={`edit-time-${row.instructorName}`}
                  >
                    <ClockIcon className="size-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={isMutating}
                    onClick={() => void removeAssist.mutateAsync(row.id)}
                    aria-label={`remove-${row.instructorName}`}
                  >
                    <Trash2Icon className="size-4 text-destructive" />
                  </Button>
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      {canMutate ? (
        <div className="space-y-2">
          <Label className="font-medium text-sm">
            {"\u0ec0\u0e9e\u0eb5\u0ec8\u0ea1\u0e84\u0eb9\u0e8a\u0ec8\u0ea7\u0e8d\u0eaa\u0ead\u0e99"}
          </Label>
          <div className="flex flex-col gap-2 sm:flex-row">
            <div className="min-w-0 flex-1">
              <InfiniteCombobox<{ id: string; name: string }>
                queryKey={["master", "instructors", "lookup", "assist"]}
                queryFn={async (params: {
                  search: string;
                  pageParam: number;
                }) => {
                  const limit = 20;
                  const skip = (params.pageParam - 1) * limit;
                  const url = new URL(
                    `${config.apiUrl}/master/instructors/lookup`,
                  );
                  url.searchParams.set("limit", String(limit));
                  url.searchParams.set("skip", String(skip));
                  if (params.search) url.searchParams.set("q", params.search);
                  return fetchLookupForInfinite<{ id: string; name: string }>(
                    url.toString(),
                    params,
                  );
                }}
                preloadQueryFn={(id) =>
                  hydrateLookupItem<{ id: string; name: string }>(
                    `${config.apiUrl}/master/instructors/lookup`,
                    id,
                  )
                }
                getLabel={(item) => item.name}
                getValue={(item) => item.id}
                value={instructorId}
                onValueChange={setInstructorId}
                placeholder={"\u0e84\u0ebb\u0ec9\u0e99\u0eab\u0eb2\u0e84\u0eb9\u0eaa\u0ead\u0e99..."}
                className="w-full"
                clearable
              />
            </div>
            <Button
              type="button"
              disabled={
                !instructorId ||
                alreadyIds.has(instructorId) ||
                addAssist.isPending
              }
              onClick={() => void handleAdd()}
            >
              {addAssist.isPending ? (
                <Loader2Icon className="mr-2 size-4 animate-spin" />
              ) : (
                <UserPlusIcon className="mr-2 size-4" />
              )}
              {"\u0ec0\u0e9e\u0eb5\u0ec8\u0ea1"}
            </Button>
          </div>
        </div>
      ) : null}

      <EditCheckInTimeDialog
        open={editTimeOpen}
        onOpenChange={setEditTimeOpen}
        target={editTimeTarget}
        onConfirm={handleConfirmEditTime}
        isSubmitting={patchTimeMutation.isPending}
      />
    </div>
  );
}
